import { Button, Checkbox, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ButtonContainer, FieldSet, IDataTableColumn, showSuccess, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import {
  DogApi,
  DogHealthTestValidationApi,
  DogHealthTestValidationGridDto,
  DogHealthTestValidationSearch,
} from "../../../api";
import { CustomDataTable, ExportButton, SharepointButton } from "../../../components";
import { useDialog } from "../../../contexts";
import { ERoutes } from "../../../ERoutes";
import { useApiService, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";

export interface IDogTransientCharacteristicsProps {
  searchUnassignedCharacteristics: boolean;
}

const StyledCheckbox = styled(Checkbox)`
  font-size: 10pt;
  top: 6px;
`;

export const DogHealthValidation: React.FunctionComponent<IDogTransientCharacteristicsProps> = ({
  searchUnassignedCharacteristics,
}) => {
  const api = useApiService(DogHealthTestValidationApi);
  const dogApi = useApiService(DogApi);
  const { t } = useTl();
  const { showDialogPromise } = useDialog();
  const [hideClosed, sethideClosed] = React.useState(true);
  const [sObjState, setsObjState] = React.useState<any>();
  const [selectionList, setselectionList] = React.useState([]);
  const editableIdsList = React.useMemo(() => [], []);
  const [selectAll, setselectAll] = React.useState(false);
  const [actionloading, setActionloading] = React.useState(false);
  const tableState = useGridState<any>({
    serverMode: true,
    availablePageSizes: [10],
    enablePagination: true,
    enableFilter: true,
    sortKeys: { dog: "ASC" },
  });
  const searchFunction = React.useCallback(
    (sObj?: DogHealthTestValidationSearch) => {
      const s = DogHealthTestValidationSearch.fromJS({
        ...sObj,
        searchValidated: !hideClosed,
        searchUnassigned: searchUnassignedCharacteristics,
      });
      setsObjState(s);
      return api.baseSearch(s);
    },
    [api, hideClosed, searchUnassignedCharacteristics]
  );
  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });
  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...editableIdsList);
    } else {
      selectionList.splice(0, selectionList.length);
    }
    setselectionList([...selectionList]);
    setselectAll(value);
  }, [editableIdsList, selectAll, selectionList]);

  const columnCheckBox = React.useCallback(
    (row: DogHealthTestValidationGridDto) => {
      if (!row.closedOn && !editableIdsList.find((c) => c === row.dogHealthTestValidationId)) {
        editableIdsList.push(row.dogHealthTestValidationId);
      }
      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.dogHealthTestValidationId)}
          onClick={() => {
            if (selectionList.includes(row.dogHealthTestValidationId)) {
              const index = selectionList.findIndex((e) => e === row.dogHealthTestValidationId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.dogHealthTestValidationId);
            }
            setselectionList([...selectionList]);
          }}
          disabled={!row.closedOn}
        />
      );
    },
    [editableIdsList, selectionList]
  );
  const [shpLoading, setShpLoading] = React.useState(null);
  const openOrCreateInShp = React.useCallback(
    async (data: DogHealthTestValidationGridDto) => {
      setShpLoading(data?.dogId);
      if (!!data?.sharepointUri) {
        window.open(data?.sharepointUri, "_blank");
        setShpLoading(null);
      } else {
        try {
          await dogApi.createInSharepoint(data?.dogId);
          search();
        } catch (e) {
          throw e;
        } finally {
          setShpLoading(null);
        }
      }
    },
    [dogApi, search]
  );
  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} checked={selectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: (row: DogHealthTestValidationGridDto) => columnCheckBox(row),
      },
      {
        header: () => t(ETLCodes.Dog),
        fieldName: "dog",
        render: (row: DogHealthTestValidationGridDto) => {
          return (
            <Link target="_blank" to={`${ERoutes.dog}/${row.dogId}/detail/view`}>
              {row.dog}
            </Link>
          );
        },
      },
      {
        header: () => t(ETLCodes.SharePoint),
        fieldName: "_action",
        render: (row: DogHealthTestValidationGridDto) => (
          <SharepointButton
            existsInShp={!!row?.sharepointUri}
            loading={shpLoading === row?.dogId}
            onClick={() => openOrCreateInShp(row)}
          />
        ),
      },
    ],
    [columnCheckBox, openOrCreateInShp, selectAll, shpLoading, t, toggleSelectAll] //Do not remove dependency, usefull for visual update
  );
  const action = React.useCallback(
    async (requestList: number[]) => {
      setActionloading(true);
      if (requestList.length === 0) {
        await showDialogPromise({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [{ resultType: "yes", text: t(ETLCodes.General_Ok), intent: Intent.PRIMARY }],
        });
      } else {
        if (searchUnassignedCharacteristics) {
          const dialogResult = await showDialogPromise({
            message: t(ETLCodes.AssignElementsQuestion),
            title: t(ETLCodes.Assign),
          });
          if (dialogResult === "yes") {
            await api.assignTask(requestList);
            selectionList.splice(0, selectionList.length);
            search();
          }
        } else {
          const dialogResult = await showDialogPromise({
            message: t(ETLCodes.ValidateElementsQuestion),
            title: t(ETLCodes.Validate),
          });
          if (dialogResult === "yes") {
            await api.validateTask(true, requestList);
            selectionList.forEach((id) => {
              var i = editableIdsList.indexOf(id);
              editableIdsList.splice(i);
            });
            setselectionList([]);
            editableIdsList.splice(0, editableIdsList.length);
            setselectAll(false);
            search();
          }
        }
      }
      setActionloading(false);
    },
    [api, editableIdsList, search, searchUnassignedCharacteristics, selectionList, showDialogPromise, t]
  );
  const refuse = React.useCallback(
    async (requestList: number[]) => {
      setActionloading(true);
      if (requestList.length === 0) {
        await showDialogPromise({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [{ resultType: "yes", text: t(ETLCodes.General_Ok), intent: Intent.PRIMARY }],
        });
      } else {
        const dialogResult = await showDialogPromise({
          message: t(ETLCodes.CloseTaskConfirmation),
          title: t(ETLCodes.Close),
        });
        if (dialogResult === "yes") {
          await api.validateTask(false, requestList);
          showSuccess(t(ETLCodes.SaveSuccess));
          search();
          selectionList.forEach((id) => {
            var i = editableIdsList.indexOf(id);
            editableIdsList.splice(i);
          });
          setselectionList([]);
          setselectAll(false);
        }
      }
      setActionloading(false);
    },
    [api, editableIdsList, search, selectionList, showDialogPromise, t]
  );
  return (
    <FieldSet
      title={
        searchUnassignedCharacteristics
          ? t(ETLCodes.HealthTestDocumentValidationToAssign)
          : t(ETLCodes.HealthTestDocumentValidationToValidate)
      }
      rightElement={
        <ButtonContainer>
          <Button
            text={searchUnassignedCharacteristics ? t(ETLCodes.Assign) : t(ETLCodes.TaskDone)}
            onClick={() => action(selectionList)}
            intent={Intent.PRIMARY}
            minimal={true}
            icon={IconNames.TICK_CIRCLE}
            loading={actionloading}
          />
          {!searchUnassignedCharacteristics && (
            <StyledCheckbox
              checked={hideClosed}
              onClick={() => {
                sethideClosed(!hideClosed);
                search();
              }}
              label={t(ETLCodes.HideClosed)}
            />
          )}
          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
        </ButtonContainer>
      }
    >
      <CustomDataTable tableState={tableState} loading={loading || actionloading} columns={columns} />
    </FieldSet>
  );
};
